/**
 * HTML Sanitization Utilities
 * Prevents XSS attacks when rendering user-generated HTML content
 */

import DOMPurify from 'dompurify'

/**
 * Sanitize HTML content to prevent XSS attacks
 * Use this before rendering any user-provided HTML with v-html
 *
 * @param html - The HTML string to sanitize
 * @returns Sanitized HTML string safe for v-html rendering
 *
 * @example
 * ```vue
 * <div v-html="sanitizeHtml(userContent)" />
 * ```
 */
export function sanitizeHtml(html: string | null | undefined): string {
  if (!html)
    return ''

  // Only run on client-side (DOMPurify needs DOM)
  if (import.meta.server)
    return html

  return DOMPurify.sanitize(html, {
    // Allow safe HTML tags for rich text content
    ALLOWED_TAGS: [
      'p',
      'br',
      'b',
      'i',
      'u',
      'strong',
      'em',
      'a',
      'ul',
      'ol',
      'li',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'blockquote',
      'code',
      'pre',
      'span',
      'div',
      'table',
      'thead',
      'tbody',
      'tr',
      'th',
      'td',
      'img',
      'hr',
      'sup',
      'sub',
    ],

    // Allow safe attributes
    ALLOWED_ATTR: [
      'href',
      'target',
      'rel',
      'src',
      'alt',
      'title',
      'class',
      'style',
      'width',
      'height',
      'colspan',
      'rowspan',
    ],

    // Force safe link attributes
    ADD_ATTR: ['target'],

    // Open links in new tab safely
    ALLOW_DATA_ATTR: false,

    // Prevent protocol-based attacks
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
  })
}

/**
 * Strict sanitization - removes ALL HTML, returns plain text
 * Use for user input that should never contain HTML
 */
export function stripHtml(html: string | null | undefined): string {
  if (!html)
    return ''

  if (import.meta.server)
    return html.replace(/<[^>]*>/g, '')

  return DOMPurify.sanitize(html, { ALLOWED_TAGS: [] })
}

/**
 * Detect actual script-injection attempts in user input.
 *
 * Returns true only for patterns that have no legitimate use in plain-text
 * form fields: <script> tags, javascript:/data:text/html URIs, and HTML
 * event handlers (onclick=, onerror=, ...). Plain '<' and '>' characters
 * (e.g. "2 > 1", "-->", "<3") are intentionally allowed.
 *
 * Output rendered via {{ }} / v-text is already auto-escaped by Vue, so
 * this function exists purely to give the user a clear error message
 * before submitting obviously malicious content.
 */
export function containsSuspiciousScript(value: string | null | undefined): boolean {
  if (!value || typeof value !== 'string')
    return false

  const patterns: RegExp[] = [
    /<\s*script\b/i,
    /<\s*\/\s*script\s*>/i,
    /<\s*iframe\b/i,
    /<\s*object\b/i,
    /<\s*embed\b/i,
    /\bjavascript\s*:/i,
    /\bdata\s*:\s*text\/html/i,
    /\bvbscript\s*:/i,
    /\son\w+\s*=\s*["'`]/i,
  ]

  return patterns.some(re => re.test(value))
}
